import React from "react";
import { Tooltip } from "@material-ui/core";
import dayjs from "dayjs";
import {
  LINE_CHART_HEIGHT,
  LINE_CHART_PADDING,
  SELECTED_BORDER,
  formatDollars,
  getCategoryCents,
  getForecastCategoryCents,
  getEffectiveCategoryCents,
  stripCategoryPrefix,
} from "./utils";

export default function LineChartView({ data, categories, colorMap, classes, selectedMonth, onSelect }) {
  const sorted = [...data].reverse();
  if (sorted.length === 0) return null;

  const svgWidth = 800;
  const svgHeight = LINE_CHART_HEIGHT;
  const { top, right, bottom, left } = LINE_CHART_PADDING;
  const plotW = svgWidth - left - right;
  const plotH = svgHeight - top - bottom;

  const maxVal = Math.max(
    ...sorted.flatMap((d) =>
      categories.map((cat) => getEffectiveCategoryCents(d, cat))
    ),
    1
  );

  const x = (i) => left + (sorted.length === 1 ? plotW / 2 : (i / (sorted.length - 1)) * plotW);
  const y = (cents) => top + plotH - (cents / maxVal) * plotH;

  const tickCount = 5;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => (maxVal / tickCount) * i);

  return (
    <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className={classes.lineChartSvg} preserveAspectRatio="xMidYMid meet">
      {/* Grid lines */}
      {ticks.map((tick) => (
        <g key={tick}>
          <line x1={left} y1={y(tick)} x2={svgWidth - right} y2={y(tick)} stroke="#e0e0e0" strokeWidth={1} />
          <text x={left - 8} y={y(tick) + 4} textAnchor="end" fontSize={11} fill="#757575">
            ${formatDollars(tick)}
          </text>
        </g>
      ))}

      {/* One line per category */}
      {categories.map((cat) => {
        const color = colorMap[cat];

        const allPoints = sorted.map((d, i) => {
          const cents = getCategoryCents(d, cat);
          const hasForecast = getForecastCategoryCents(d, cat) > 0;
          return { x: x(i), y: y(cents), cents, hasForecast, index: i };
        });

        const solidPoints = allPoints.filter((p) => !p.hasForecast);
        const solidPath = solidPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

        return (
          <g key={cat}>
            {solidPoints.length > 0 && (
              <path d={solidPath} fill="none" stroke={color} strokeWidth={2.5} />
            )}

            {/* Dashed lines for forecast months */}
            {sorted.map((d, i) => {
              const forecastCatCents = getForecastCategoryCents(d, cat);
              if (forecastCatCents === 0) return null;
              const billedCents = getCategoryCents(d, cat);
              const effectiveCents = billedCents + forecastCatCents;

              return (
                <g key={`forecast-${d.month}`}>
                  {i > 0 && (
                    <line
                      x1={allPoints[i - 1].x}
                      y1={allPoints[i - 1].y}
                      x2={x(i)}
                      y2={y(effectiveCents)}
                      stroke={color}
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      opacity={0.5}
                    />
                  )}
                  <Tooltip title={`${stripCategoryPrefix(cat)} (forecast): $${formatDollars(forecastCatCents)}`}>
                    <circle
                      cx={x(i)}
                      cy={y(effectiveCents)}
                      r={d.month === selectedMonth ? 5 : 3}
                      fill="white"
                      stroke={color}
                      strokeWidth={2}
                      strokeDasharray="3 2"
                      className={classes.lineChartDot}
                      onClick={() => onSelect(d.month)}
                    />
                  </Tooltip>
                </g>
              );
            })}

            {/* Billed dots (only for statemented months) */}
            {allPoints.filter((p) => !p.hasForecast).map((p) => (
              <Tooltip
                key={sorted[p.index].month}
                title={`${stripCategoryPrefix(cat)}: $${formatDollars(p.cents)}`}
              >
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={sorted[p.index].month === selectedMonth ? 6 : 4}
                  fill={color}
                  stroke="white"
                  strokeWidth={2}
                  className={classes.lineChartDot}
                  onClick={() => onSelect(sorted[p.index].month)}
                />
              </Tooltip>
            ))}
          </g>
        );
      })}

      {/* Selected month highlight + x-axis labels */}
      {sorted.map((d, i) => {
        const isSelected = d.month === selectedMonth;
        return (
          <g key={d.month} onClick={() => onSelect(d.month)} style={{ cursor: "pointer" }}>
            {isSelected && (
              <line x1={x(i)} y1={top} x2={x(i)} y2={top + plotH} stroke={SELECTED_BORDER} strokeWidth={1} strokeDasharray="3 3" opacity={0.4} />
            )}
            <rect x={x(i) - 30} y={top} width={60} height={plotH + bottom} fill="transparent" />
            <text
              x={x(i)}
              y={svgHeight - 8}
              textAnchor="middle"
              fontSize={11}
              fill={isSelected ? SELECTED_BORDER : "#757575"}
              fontWeight={isSelected ? 700 : 400}
            >
              {dayjs(d.month).format("MMM YYYY")}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
